// group-chat.js
// The single chat implementation for both 1:1 and multi-party
// conversations over real libp2p connections — chat.js used to handle
// 1:1 separately, with its own protocol and its own from-scratch
// concurrency-safety work; it's retired now (see startDirectChat below)
// in favor of treating 1:1 as exactly what MLS already considers it: a
// 2-member group. One protocol to reason about and audit instead of
// two, same reasoning as retiring the original hand-rolled session.js
// handshake earlier in this project's history.
//
// Topology: full mesh. Every group member eventually holds a direct
// pairwise libp2p connection to every other member — no relay, no single
// point of failure, consistent with the rest of this project having no
// server. For team-ideation-sized groups (a handful to a couple dozen
// people — the same assumption made when choosing MLS's pairwise-cost
// tradeoffs originally) O(N^2) connections is entirely reasonable; this
// isn't meant to scale to huge broadcast groups. A 1:1 direct chat is
// just the N=2 case — no special-casing needed for the mesh logic.
//
// Why broadcasting is just "send the same bytes N times": an MLS
// application message or commit is a single ciphertext blob any current
// member can process with their own key material — unlike the old
// pairwise-fan-out idea, there's no need to re-encrypt per recipient.
// Only the welcome (for a specific new member) is recipient-specific.
//
// Wire protocol (JSON envelopes over libp2p v3's stream.send()/async
// -iterate). Every stream, whatever role it ends up playing, is read by
// exactly ONE `for await` loop for its entire lifetime (_streamLoop) —
// splitting reads across multiple sequential loops on the same stream is
// unsafe (exiting a `for await` calls the iterator's `.return()`, which
// can prevent a second loop from picking up where the first left off).
// A stream-keyed waiter map is how invite() gets the 'keypackage'
// response back out of that single loop without needing a loop of its
// own.
//
//   'request-keypackage' -> 'keypackage'   (inviter <-> prospective member)
//   'welcome'                              (inviter -> new member only;
//                                            includes a roster so the new
//                                            member can mesh out to
//                                            everyone else directly)
//   'commit'                               (inviter -> every OTHER
//                                            existing member, broadcast)
//   'member-hello'                         (new member -> every other
//                                            existing member, to complete
//                                            the mesh)
//   'data'                                 (any member -> every other
//                                            member, broadcast)
//
// Security note on 'member-hello': accepting one just registers a stream
// to try — it does NOT itself grant any trust. The actual security
// boundary is MLS's own cryptography: if whoever sent it isn't really a
// current group member, their subsequent 'data'/'commit' bytes simply
// fail to decrypt/verify in group.receiveMessage() and get rejected. No
// separate manual roster-membership check is needed on top of that — it
// would be redundant with, not additional to, the real cryptographic
// guarantee.
//
// Concurrency, multi-party mesh formation: two members could race
// independently dialing each other while forming the mesh. Fixed with a
// synchronous `pending` reservation per (groupId, peerIdStr) closing the
// check-then-act gap, plus an identity-based tie-break (lower identity
// dials) so in the common case only one side ever initiates at all.
//
// Concurrency, direct (1:1) chat: startDirectChat() sidesteps this whole
// class of race by construction rather than needing the same kind of
// patch. invite() has exactly one inviter role — dial, request a key
// package, add the member, send a welcome — so as long as only the
// lower-identity peer of a pair ever calls it (the tie-break in
// startDirectChat), there's never a scenario where both sides
// independently dial for the same direct chat. This is a real
// simplification chat.js didn't have available to it: chat.js allowed
// fully symmetric connect() calls from both sides (matching an
// arbitrary caller's dial() before this project settled on
// startDirectChat's asymmetric-by-design model), which is what forced
// its TOCTOU-safe pending reservation and "don't resolve early" fixes in
// the first place.
//
// PeerId handling: every group.members entry stores the real PeerId
// object (`remotePeer`), not just its string form — needed for
// node.getConnections()/dialProtocol(), which require an actual PeerId,
// not a bare string (DialTarget = PeerId | Multiaddr | Multiaddr[], per
// libp2p's own types). Wherever only a string arrives over the wire (a
// roster entry naming a peer we haven't connected to yet),
// peerIdFromString() reconstructs the object before it's used to dial.

import { EventEmitter } from 'node:events'
import { multiaddr } from '@multiformats/multiaddr'
import { peerIdFromString } from '@libp2p/peer-id'
import * as grp from './group.js'

export const PROTOCOL = '/p2p-group-chat/1.0.0'

const textEncode = (obj) => new TextEncoder().encode(JSON.stringify(obj))
const textDecode = (chunk) => {
  const bytes = chunk.subarray ? chunk.subarray() : chunk
  return JSON.parse(new TextDecoder().decode(bytes))
}
const b64 = (bytes) => Buffer.from(bytes).toString('base64')
const unb64 = (str) => new Uint8Array(Buffer.from(str, 'base64'))
const idHex = (identity) => Buffer.from(identity.publicKey.raw).toString('hex')

// Deterministic groupId for a direct (1:1) chat between two peers,
// computable by both sides immediately from libp2p PeerIds alone — no
// prior exchange needed, and both sides always agree on the same value
// regardless of who's asking. This is what makes 1:1 "just a 2-member
// group" rather than a separate protocol (see startDirectChat below,
// and the module comment on why this replaces the old chat.js).
export function directChatGroupId (peerIdStrA, peerIdStrB) {
  return 'direct:' + [peerIdStrA, peerIdStrB].sort().join(':')
}

export class GroupChatManager extends EventEmitter {
  /**
   * @param {import('libp2p').Libp2p} node
   * @param {{privateKey: any, publicKey: any}} identity
   * @param {object} mlsCtx - from group.makeMlsContext(trustStore, onNewIdentity)
   * @param {object} [opts]
   * @param {{save: (snapshot: object) => Promise<void>}} [opts.stateStore] - if
   *   given, the complete group state is saved through it every time it
   *   changes, and BEFORE any ciphertext that depends on the change leaves
   *   this machine (see _persist). Without one nothing survives a restart.
   * @param {(peerIdStr: string) => string[]} [opts.addressesFor] - multiaddrs
   *   to try when re-attaching to a member we have no live stream to (e.g. the
   *   relay path for a browser peer). Used by resumeGroups().
   */
  constructor (node, identity, mlsCtx, { stateStore = null, addressesFor = () => [] } = {}) {
    super()
    this.node = node
    this.identity = identity
    this.mlsCtx = mlsCtx
    this.stateStore = stateStore
    this.addressesFor = addressesFor
    this._persistChain = Promise.resolve()
    // groupId -> promise tail; see _withGroupLock
    this._groupLocks = new Map()
    this.ourIdentityHex = idHex(identity)
    // groupId -> { state, ownKp, members: Map<peerIdStr, {stream, identityHex, remotePeer}> }
    this.groups = new Map()
    // `${groupId}:${peerIdStr}` -> true while a mesh connection attempt is
    // in flight, closing the same check-then-act gap chat.js's `pending`
    // Set closes for 1:1 pairing.
    this.pending = new Set()
    // stream -> { type, resolve } — how invite() gets a response out of
    // the single shared per-stream read loop below.
    this.waiters = new Map()
    // groupId -> array of {remotePeerIdStr, stream, remotePeer} — a
    // 'member-hello' can legitimately arrive before OUR OWN joinGroup()
    // for that group has finished (MLS join crypto vs. a same-moment
    // commit-processing path on the sender's side is a real race), so it
    // can't just be dropped when this.groups.get(groupId) is still
    // empty. Queued here, drained once the 'welcome' handler actually
    // creates the group entry.
    this.pendingMeshHellos = new Map()
    // groupId -> true while WE are mid-createGroup+invite for a direct
    // chat we're responsible for founding — guards against a redundant
    // second attempt for the same peer while the first is still in
    // flight, whether triggered by our own startDirectChat() call or by
    // a 'request-direct-chat' from the other side (see _foundDirectChat).
    this.pendingDirectChats = new Set()
    // groupId -> true while we've asked the other (lower-identity) peer
    // to found a direct chat and are waiting for their dial back —
    // guards against sending a duplicate request if startDirectChat()
    // is called again before the first request's stream even finishes.
    this.pendingDirectChatRequests = new Set()

    node.handle(PROTOCOL, (stream, connection) => {
      this._streamLoop(stream, connection.remotePeer)
        .catch((err) => this.emit('error', err, connection.remotePeer.toString()))
    })
  }

  /** Create a brand new group with just yourself as a member. Returns the groupId. */
  async createGroup (groupIdStr) {
    if (this.groups.has(groupIdStr)) throw new Error(`Already in group ${groupIdStr}`)
    const ownKp = await grp.generateOwnKeyPackage(this.identity, this.mlsCtx)
    const state = await grp.createGroup(this.mlsCtx, groupIdStr, ownKp)
    this.groups.set(groupIdStr, { state, ownKp, members: new Map() })
    await this._persist()
    return groupIdStr
  }

  // ---- Persistence ---------------------------------------------------------
  //
  // What is saved: per group, the complete MLS state (ratchet tree, secret
  // tree, key schedule, our private path and signing key) plus who the other
  // members are. Streams are NOT saved — they are live connections — so a
  // restored group has members but no way to reach them until resumeGroups()
  // re-attaches.
  //
  // Why saving happens BEFORE sending: every message we send consumes a key
  // from our sending ratchet. If we sent, crashed before saving, and restored
  // an older state, we would reuse a key and nonce for different plaintext,
  // which breaks AES-GCM. (A peer that has already seen that generation
  // refuses it — see tests/group-state-persistence-test.mjs — but that only
  // protects the peer's reading, not what an observer could learn from the
  // reuse.) Receiving is the safe direction: a state restored older than the
  // peer's still reads their newer messages, so received messages are saved
  // but a failed save doesn't stop us.

  /**
   * Run `fn` with exclusive access to a group's MLS state. Every operation
   * that reads group.state, awaits crypto, and assigns the result back (send,
   * receive, commit, invite) must go through this. Without it two of them can
   * start from the same old state, and whichever finishes last overwrites the
   * other's progress: a receive landing while we send rewinds our sending
   * ratchet, and the next message reuses a generation the peer has already
   * seen ("Desired gen in the past"). It was a latent race in live chats that
   * only showed once both sides sent at nearly the same moment.
   */
  _withGroupLock (groupId, fn) {
    const tail = this._groupLocks.get(groupId) ?? Promise.resolve()
    const run = tail.then(fn, fn)
    this._groupLocks.set(groupId, run.catch(() => {}))
    return run
  }

  /** A JSON-safe snapshot of every group. Contains private key material: encrypt it at rest. */
  exportState () {
    return {
      version: 1,
      groups: [...this.groups.entries()].map(([groupId, group]) => ({
        groupId,
        state: b64(grp.exportGroupState(group.state)),
        members: [...group.members.entries()].map(([peerIdStr, m]) => ({ peerIdStr, identityHex: m.identityHex ?? null }))
      }))
    }
  }

  /**
   * Restore groups from exportState(). Groups already present are left alone.
   * Restored members have no stream until resumeGroups() attaches one.
   * @returns {string[]} the group IDs restored
   */
  importState (snapshot) {
    if (!snapshot || snapshot.version !== 1) throw new Error('Unrecognized saved group state')
    const restored = []
    for (const g of snapshot.groups) {
      if (this.groups.has(g.groupId)) continue
      this.groups.set(g.groupId, {
        state: grp.importGroupState(this.mlsCtx, unb64(g.state)),
        ownKp: null,
        members: new Map(g.members.map((m) => [m.peerIdStr, { stream: null, identityHex: m.identityHex ?? null, remotePeer: peerIdFromString(m.peerIdStr) }]))
      })
      restored.push(g.groupId)
    }
    for (const groupId of restored) this.emit('restored', groupId)
    return restored
  }

  /**
   * Save the current state through the stateStore, if any. Writes are queued
   * so they land in order, and each one snapshots at the time it runs so the
   * latest state always wins. Rejects if the save fails — callers that must not
   * proceed without a durable state (anything that sends) let it propagate.
   */
  _persist () {
    if (!this.stateStore) return Promise.resolve()
    const run = () => this.stateStore.save(this.exportState())
    const result = this._persistChain.then(run, run)
    this._persistChain = result.catch(() => {})
    return result
  }

  /** Non-critical saves (membership bookkeeping): failures are reported, not thrown. */
  _persistQuietly () {
    this._persist().catch((err) => this.emit('error', err))
  }

  /** True if this member has a usable stream right now. */
  _isLive (member) {
    return !!member.stream && (member.stream.status === undefined || member.stream.status === 'open')
  }

  /**
   * Re-attach to members we have no live stream to — after a restart (restored
   * groups have none), or after a connection died. Dials each one and sends the
   * same 'member-hello' used to form the mesh; the other side swaps in the new
   * stream. Safe to call repeatedly, and if both sides call it at once each
   * ends up reading both streams, so messages still flow.
   * @returns {Promise<{resumed: string[], failed: string[]}>}
   */
  async resumeGroups (addressesFor = this.addressesFor) {
    const resumed = []
    const failed = []
    const work = []
    for (const [groupId, group] of this.groups) {
      for (const [peerIdStr, member] of group.members) {
        if (this._isLive(member)) continue
        const key = `${groupId}:${peerIdStr}`
        if (this.pending.has(key)) continue
        this.pending.add(key)
        work.push((async () => {
          try {
            await this._dialAndHello(groupId, { peerIdStr, identityHex: member.identityHex, multiaddrs: addressesFor(peerIdStr) }, { force: true })
            resumed.push(key)
          } catch (err) {
            failed.push(key)
            this.emit('resume-failed', groupId, peerIdStr, err)
          } finally {
            this.pending.delete(key)
          }
        })())
      }
    }
    await Promise.all(work)
    return { resumed, failed }
  }

  /**
   * Start (or reuse) a 1:1 conversation with a peer — the direct
   * replacement for the old chat.js's connect(). Under the hood this is
   * just a 2-member group with a deterministic ID (directChatGroupId),
   * so it reuses createGroup/invite rather than being a separate
   * protocol.
   *
   * Only the lower-identity peer of the pair ever actually founds the
   * group (calls createGroup+invite) — that part sidesteps the
   * double-connection race chat.js needed dedicated machinery for, by
   * construction. But founding can be triggered two ways: by that
   * peer's own startDirectChat() call, OR by the higher-identity peer
   * calling startDirectChat() first and sending a lightweight
   * 'request-direct-chat' asking the lower peer to found it — this
   * second path matters because founder-eligibility is based on
   * identity comparison, not on who happens to call this method first;
   * without it, a higher-identity peer calling this alone (the normal,
   * non-racing case — nobody else has called anything) would simply
   * wait forever with no one ever inviting it.
   *
   * Resolves once the chat is ready to send/receive; safe to call
   * redundantly (returns the existing groupId if already set up, or
   * waits on an in-flight attempt rather than starting a second one).
   * @param {import('@libp2p/interface').PeerId} peerId
   * @returns {Promise<string>} the groupId — pass to send()/sendDirect()
   */
  async startDirectChat (peerId) {
    const theirPeerIdStr = peerId.toString()
    const ourPeerIdStr = this.node.peerId.toString()
    const groupId = directChatGroupId(ourPeerIdStr, theirPeerIdStr)

    if (this.groups.has(groupId)) {
      // Already have this chat — possibly restored from a saved snapshot, in
      // which case there is no stream to the peer yet. Re-attach rather than
      // returning a group we can't actually talk through.
      const member = this.groups.get(groupId).members.get(theirPeerIdStr)
      if (member && !this._isLive(member)) {
        await this._dialAndHello(groupId, { peerIdStr: theirPeerIdStr, identityHex: member.identityHex, multiaddrs: this.addressesFor(theirPeerIdStr) }, { force: true })
      }
      return groupId
    }

    const weAreLower = ourPeerIdStr < theirPeerIdStr
    if (weAreLower) {
      return this._foundDirectChat(groupId, peerId)
    }

    // We're not the founder — ask the lower peer to found it. They'll
    // dial us back (via invite(), a separate stream) with the actual
    // welcome; this request stream's only job is the notification, so
    // it's closed immediately after sending rather than kept open.
    if (!this.pendingDirectChatRequests.has(groupId)) {
      this.pendingDirectChatRequests.add(groupId)
      try {
        const stream = await this.node.dialProtocol(peerId, PROTOCOL)
        stream.send(textEncode({ type: 'request-direct-chat', groupId }))
        await stream.close().catch(() => {})
      } finally {
        this.pendingDirectChatRequests.delete(groupId)
      }
    }

    return this._awaitGroupJoined(groupId)
  }

  /**
   * The actual founding work for a direct chat — createGroup + invite.
   * Deduplicated via pendingDirectChats regardless of whether it was
   * triggered by our own startDirectChat() or by receiving a
   * 'request-direct-chat' from the other side (see _streamLoop) — both
   * paths funnel through here, which is what makes the simultaneous-call
   * race safe: whichever trigger arrives second sees the reservation
   * already in place and just waits, rather than founding twice.
   */
  async _foundDirectChat (groupId, peerId) {
    if (this.groups.has(groupId)) return groupId
    if (this.pendingDirectChats.has(groupId)) {
      return this._awaitGroupJoined(groupId)
    }
    this.pendingDirectChats.add(groupId)
    try {
      await this.createGroup(groupId)
      try {
        await this.invite(groupId, peerId)
      } catch (err) {
        // createGroup() already registered this groupId in this.groups —
        // if invite() then fails partway through (dial error, keypackage
        // request timeout, or any other failure after the group exists
        // but before the peer is actually added), leaving it registered
        // would permanently block every future retry: startDirectChat's
        // very first check is `if (this.groups.has(groupId)) return
        // groupId`, so a retry would silently return this broken,
        // memberless group instead of actually trying again. Roll back
        // so a retry starts genuinely fresh.
        this.groups.delete(groupId)
        throw err
      }
      return groupId
    } finally {
      this.pendingDirectChats.delete(groupId)
    }
  }

  /** Convenience: send to a direct chat by peer, instead of by groupId. */
  async sendDirect (peerId, plaintext) {
    const groupId = directChatGroupId(this.node.peerId.toString(), peerId.toString())
    if (!this.groups.has(groupId)) {
      throw new Error(`No active direct chat with ${peerId.toString()} — call startDirectChat() first`)
    }
    return this.send(groupId, plaintext)
  }

  /** Wait for a group we didn't found ourselves to be joined (fired by the 'welcome' handler in _streamLoop). */
  _awaitGroupJoined (groupId, timeoutMs = 30000) {
    if (this.groups.has(groupId)) return Promise.resolve(groupId)
    return new Promise((resolve, reject) => {
      const onJoined = (gid) => {
        if (gid !== groupId) return
        this.off('joined', onJoined)
        clearTimeout(timer)
        resolve(groupId)
      }
      this.on('joined', onJoined)
      const timer = setTimeout(() => {
        this.off('joined', onJoined)
        reject(new Error(`Timed out waiting to be invited into ${groupId}`))
      }, timeoutMs)
    })
  }

  /** Invite a peer (by libp2p PeerId) into a group you're already in. */
  async invite (groupIdStr, peerId) {
    const group = this.groups.get(groupIdStr)
    if (!group) throw new Error(`Not in group ${groupIdStr}`)
    const remotePeerIdStr = peerId.toString()

    const stream = await this.node.dialProtocol(peerId, PROTOCOL)
    const loopPromise = this._streamLoop(stream, peerId)
      .catch((err) => this.emit('error', err, remotePeerIdStr))

    // No timeout here previously - a stalled exchange (the other side
    // never responds, e.g. their own stream got disrupted) would hang
    // this Promise forever rather than throwing, and _foundDirectChat's
    // rollback (see its own comment) only runs when invite() actually
    // throws. A hung, non-throwing invite() would leave this groupId
    // stuck in pendingDirectChats permanently, silently blocking every
    // future retry - including ones from the OTHER peer, whose own
    // retry would just wait on this same never-completing attempt.
    const theirKpBytesPromise = new Promise((resolve, reject) => {
      this.waiters.set(stream, { type: 'keypackage', resolve })
      setTimeout(() => {
        if (this.waiters.get(stream)?.resolve !== resolve) return // already resolved
        this.waiters.delete(stream)
        reject(new Error(`Timed out waiting for keypackage from ${remotePeerIdStr}`))
      }, 20000)
    })
    stream.send(textEncode({ type: 'request-keypackage', groupId: groupIdStr }))
    const theirKpBytes = await theirKpBytesPromise

    const theirKp = grp.decodeKeyPackage(theirKpBytes)
    const theirIdentityHex = Buffer.from(grp.identityFromKeyPackage(theirKp)).toString('hex')

    const added = await this._withGroupLock(groupIdStr, async () => {
      const result = await grp.addMembers(this.mlsCtx, group.state, [theirKpBytes])
      group.state = result.newState
      await this._persist() // before the welcome/commit go out
      return result
    })

    const roster = [...group.members.entries()].map(([pid, m]) => ({
      peerIdStr: pid,
      identityHex: m.identityHex,
      multiaddrs: this._knownMultiaddrsFor(m.remotePeer)
    }))

    stream.send(textEncode({
      type: 'welcome',
      groupId: groupIdStr,
      welcomeBytes: b64(added.welcomeBytes),
      inviterIdentityHex: this.ourIdentityHex,
      roster
    }))

    for (const [, m] of group.members) {
      m.stream.send(textEncode({
        type: 'commit',
        groupId: groupIdStr,
        commitBytes: b64(added.commitBytes),
        // So existing members can ALSO mesh out to the new member when
        // the identity tie-break favors them — mesh formation triggered
        // only from the new member's side (via the 'welcome' roster)
        // would silently fail to connect whenever the new member happens
        // to have the higher identity, since the tie-break would then
        // say "not our turn to dial" on both sides with nobody left to
        // initiate. See the 'commit' handler in _streamLoop.
        newMember: { peerIdStr: remotePeerIdStr, identityHex: theirIdentityHex, multiaddrs: this._knownMultiaddrsFor(peerId) }
      }))
    }

    group.members.set(remotePeerIdStr, { stream, identityHex: theirIdentityHex, remotePeer: peerId })
    this._persistQuietly() // the member list changed
    this.emit('member-added', groupIdStr, remotePeerIdStr)
    // loopPromise is intentionally NOT awaited here — it resolves only
    // once this stream eventually ends, and this stream is now the
    // member's ongoing broadcast channel for the group's whole lifetime.
    void loopPromise
  }

  /** Send a plaintext message to every current member of the group. */
  async send (groupIdStr, plaintext) {
    const group = this.groups.get(groupIdStr)
    if (!group) throw new Error(`Not in group ${groupIdStr}`)
    const members = [...group.members.entries()]
    const live = members.filter(([, m]) => this._isLive(m))
    // Refuse BEFORE touching the ratchet: with nobody to deliver to, sending
    // would only burn a key and tell the user a message went out that didn't.
    if (members.length > 0 && live.length === 0) {
      this.resumeGroups().catch(() => {}) // try to get reattached for next time
      throw new Error('No connected members — the message was not sent')
    }
    const result = await this._withGroupLock(groupIdStr, async () => {
      const sent = await grp.sendMessage(this.mlsCtx, group.state, plaintext)
      group.state = sent.newState
      await this._persist() // write-ahead: the key is spent, so record that before anything leaves
      return sent
    })
    const payload = textEncode({ type: 'data', groupId: groupIdStr, bytes: b64(result.encoded) })
    for (const [, m] of live) m.stream.send(payload)
    for (const [peerIdStr, m] of members) if (!this._isLive(m)) this.emit('undelivered', groupIdStr, peerIdStr)
  }

  /** @param {import('@libp2p/interface').PeerId} [remotePeer] */
  _knownMultiaddrsFor (remotePeer) {
    if (!remotePeer) return []
    const conns = this.node.getConnections?.(remotePeer) ?? []
    return conns.map((c) => c.remoteAddr?.toString()).filter(Boolean)
  }

  /**
   * ONE read loop per stream, for that stream's entire lifetime —
   * whatever role the stream ends up playing (inviter's exchange with a
   * prospective member, an established member's ongoing broadcast
   * channel, or a mesh-formation connection). See module comment for why
   * this must not be split across multiple loops.
   * @param {import('@libp2p/interface').PeerId} remotePeer
   */
  async _streamLoop (stream, remotePeer) {
    const remotePeerIdStr = remotePeer.toString()
    let ownKpForPendingWelcome = null

    for await (const chunk of stream) {
      const msg = textDecode(chunk)

      const waiter = this.waiters.get(stream)
      if (waiter && msg.type === waiter.type) {
        this.waiters.delete(stream)
        waiter.resolve(msg.type === 'keypackage' ? unb64(msg.bytes) : msg)
        continue
      }

      if (msg.type === 'request-keypackage') {
        const ownKp = await grp.generateOwnKeyPackage(this.identity, this.mlsCtx)
        ownKpForPendingWelcome = ownKp
        stream.send(textEncode({ type: 'keypackage', bytes: b64(grp.encodeKeyPackage(ownKp.publicPackage)) }))
        continue
      }

      if (msg.type === 'welcome') {
        const state = await grp.joinGroup(this.mlsCtx, unb64(msg.welcomeBytes), ownKpForPendingWelcome, undefined)
        this.groups.set(msg.groupId, {
          state,
          ownKp: ownKpForPendingWelcome,
          members: new Map([[remotePeerIdStr, { stream, identityHex: msg.inviterIdentityHex, remotePeer }]])
        })
        await this._persist().catch((err) => this.emit('error', err))
        this.emit('joined', msg.groupId)
        const queued = this.pendingMeshHellos.get(msg.groupId)
        if (queued) {
          this.pendingMeshHellos.delete(msg.groupId)
          for (const q of queued) this._registerMeshMember(msg.groupId, q.remotePeerIdStr, q.stream, q.remotePeer)
        }
        this._meshOutTo(msg.groupId, msg.roster).catch((err) => this.emit('error', err))
        continue
      }

      if (msg.type === 'request-direct-chat') {
        // The higher-identity peer of a direct-chat pair is asking us
        // (the lower one) to found it — see startDirectChat's comment
        // for why this exists. Funnels through the same _foundDirectChat
        // used by our own startDirectChat() call, so a simultaneous call
        // on both sides still only founds once.
        this._foundDirectChat(msg.groupId, remotePeer).catch((err) => this.emit('error', err, remotePeerIdStr))
        continue
      }

      if (msg.type === 'member-hello') {
        const group = this.groups.get(msg.groupId)
        if (!group) {
          // Haven't finished joining this group ourselves yet — queue
          // rather than drop (see constructor comment on pendingMeshHellos).
          const queue = this.pendingMeshHellos.get(msg.groupId) ?? []
          queue.push({ remotePeerIdStr, stream, remotePeer })
          this.pendingMeshHellos.set(msg.groupId, queue)
          continue
        }
        this._registerMeshMember(msg.groupId, remotePeerIdStr, stream, remotePeer)
        continue
      }

      if (msg.type === 'commit') {
        const group = this.groups.get(msg.groupId)
        if (!group) continue
        await this._withGroupLock(msg.groupId, async () => {
          const result = await grp.receiveMessage(this.mlsCtx, group.state, unb64(msg.commitBytes))
          group.state = result.newState
          await this._persist().catch((err) => this.emit('error', err)) // a missed epoch change would strand us
        })
        this.emit('membership-changed', msg.groupId)
        // Mirror image of the 'welcome' handler's _meshOutTo: THAT covers
        // the new member dialing existing ones; this covers existing
        // members dialing the new one, for whichever direction the
        // identity tie-break actually favors (see module comment).
        if (msg.newMember) {
          this._meshOutTo(msg.groupId, [msg.newMember]).catch((err) => this.emit('error', err))
        }
        continue
      }

      if (msg.type === 'data') {
        const group = this.groups.get(msg.groupId)
        if (!group) continue
        try {
          const result = await this._withGroupLock(msg.groupId, async () => {
            const received = await grp.receiveMessage(this.mlsCtx, group.state, unb64(msg.bytes))
            group.state = received.newState
            await this._persist().catch((err) => this.emit('error', err))
            return received
          })
          if (result.plaintext !== undefined) {
            this.emit('message', msg.groupId, remotePeerIdStr, result.plaintext)
          }
        } catch (err) {
          this.emit('error', err, remotePeerIdStr)
        }
        continue
      }
    }

    this.waiters.delete(stream)
  }

  _registerMeshMember (groupIdStr, remotePeerIdStr, stream, remotePeer) {
    const group = this.groups.get(groupIdStr)
    if (!group) return // not (yet) in this group ourselves — ignore
    const existing = group.members.get(remotePeerIdStr)
    if (existing && this._isLive(existing) && existing.stream !== stream) {
      // Two live streams to the same peer — both sides dialed at once, or the
      // peer reconnected before we noticed the old one die. Keep the existing
      // one for sending but leave this one open: it still has a read loop, so
      // anything the peer sends on it is received.
      return
    }
    // A missing or dead stream is replaced by the newer one. This is safe
    // because `remotePeer` is the libp2p-authenticated peer ID of the
    // connection this arrived on: only the real member can swap in a stream
    // under its own name. (Before, a hello for a known member was refused,
    // which left a restarted peer unable to re-attach to one that still held
    // its dead stream.)
    // identityHex is confirmed once this peer's traffic actually decrypts
    // (see module comment on 'member-hello') rather than trusted upfront.
    group.members.set(remotePeerIdStr, { stream, identityHex: existing?.identityHex ?? null, remotePeer })
    this._persistQuietly()
  }

  /** After joining via welcome, connect out to every other roster member to complete the mesh. */
  async _meshOutTo (groupIdStr, roster) {
    for (const entry of roster) {
      const key = `${groupIdStr}:${entry.peerIdStr}`
      const weAreLower = this.ourIdentityHex < entry.identityHex
      if (!weAreLower) continue // the lower-identity side initiates — same tie-break pattern as chat.js
      if (this.pending.has(key)) continue
      this.pending.add(key)
      try {
        await this._dialAndHello(groupIdStr, entry)
      } catch (err) {
        this.emit('error', err, entry.peerIdStr)
      } finally {
        this.pending.delete(key)
      }
    }
  }

  async _dialAndHello (groupIdStr, entry, { force = false } = {}) {
    const group = this.groups.get(groupIdStr)
    if (!group) return
    const existing = group.members.get(entry.peerIdStr)
    // Normal mesh formation never replaces a member. Re-attaching (force)
    // does, but only when there is no live stream to lose.
    if (existing && (!force || this._isLive(existing))) return

    const remotePeer = peerIdFromString(entry.peerIdStr)
    const addrs = (entry.multiaddrs ?? []).map((a) => multiaddr(a))
    const dialTarget = addrs.length > 0 ? addrs : remotePeer
    const stream = await this.node.dialProtocol(dialTarget, PROTOCOL, { signal: AbortSignal.timeout(30000) })
    group.members.set(entry.peerIdStr, { stream, identityHex: entry.identityHex ?? existing?.identityHex ?? null, remotePeer })
    this._persistQuietly()
    this._streamLoop(stream, remotePeer).catch((err) => this.emit('error', err, entry.peerIdStr))
    stream.send(textEncode({ type: 'member-hello', groupId: groupIdStr, identityHex: this.ourIdentityHex }))
  }
}
